/**
 * Turn an API failure into something worth showing a user.
 *
 * `fetchWithAuth` throws an Error whose message wraps the response body, so the useful
 * part — the Lambda's `error` string and any `validationErrors` list — is buried inside a
 * JSON fragment. Without unwrapping it the user sees a raw status line and the server's
 * actual explanation ("A legitimate interests assessment is required when relying on
 * Article 6(1)(f)") is lost, which is the difference between an actionable message and a
 * shrug.
 *
 * Extracted here because this logic was already duplicated inside
 * `pages/minutes/MeetingMinutesDetail.tsx`. New callers should import it; the existing
 * local copy should migrate the next time that file is touched.
 */
export const describeApiError = (err: unknown, fallback: string): string => {
  const message = err instanceof Error ? err.message : '';
  const jsonStart = message.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const body = JSON.parse(message.slice(jsonStart)) as {
        error?: string;
        validationErrors?: string[];
      };
      if (body.error) {
        const detail = body.validationErrors?.length
          ? ` ${body.validationErrors.join(' ')}`
          : '';
        return `${body.error}.${detail}`;
      }
    } catch {
      /* not a JSON error body — fall through */
    }
  }
  return message || fallback;
};
